import React, { useState } from 'react';
import { 
    User, Building, Globe, Moon, Sun, Bell, 
    LogOut, Crown, Check, ChevronRight, Shield, ExternalLink,
    Users, Package, MessageSquareWarning
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import MobileLayout from '../components/layout/MobileLayout';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { cn } from '../lib/utils';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { requestNotificationPermission } from '../lib/firebase';

const PLANS = [
    {
        id: 'free',
        name: 'Free',
        nameHi: 'फ्री',
        price: 0,
        features: [
            { en: '50 transactions/month', hi: '50 लेन-देन/माह' },
            { en: 'Basic risk alerts', hi: 'बेसिक जोखिम अलर्ट' },
            { en: 'Daily reports', hi: 'दैनिक रिपोर्ट' },
        ]
    },
    {
        id: 'pro',
        name: 'Pro',
        nameHi: 'प्रो',
        price: 299,
        popular: true,
        features: [
            { en: 'Unlimited transactions', hi: 'असीमित लेन-देन' },
            { en: 'Advanced AI risk scoring', hi: 'एडवांस्ड AI जोखिम स्कोरिंग' },
            { en: 'Voice entry', hi: 'आवाज़ से एंट्री' },
            { en: 'Credit tracker', hi: 'क्रेडिट ट्रैकर' },
            { en: 'WhatsApp sharing', hi: 'WhatsApp शेयरिंग' },
            { en: 'Priority support', hi: 'प्राथमिकता सहायता' },
        ]
    },
    {
        id: 'business',
        name: 'Business',
        nameHi: 'बिज़नेस',
        price: 999,
        features: [
            { en: 'Everything in Pro', hi: 'Pro की सभी सुविधाएं' },
            { en: 'Multi-user access', hi: 'मल्टी-यूज़र एक्सेस' },
            { en: 'API access', hi: 'API एक्सेस' },
            { en: 'Team management', hi: 'टीम मैनेजमेंट' },
            { en: 'Custom reports', hi: 'कस्टम रिपोर्ट्स' },
            { en: 'Dedicated support', hi: 'समर्पित सहायता' },
        ]
    }
];

const Settings = () => {
    const { user, updateProfile, logout, authAxios } = useAuth();
    const { t, language, toggleLanguage } = useLanguage();
    const { theme, toggleTheme, isDark } = useTheme();
    const navigate = useNavigate();
    
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
    const [alertsModalOpen, setAlertsModalOpen] = useState(false);
    const [formData, setFormData] = useState({
        business_name: user?.business_name || '',
        owner_name: user?.owner_name || ''
    });
    const [saving, setSaving] = useState(false);
    const [notificationsEnabled, setNotificationsEnabled] = useState(false);
    const [enablingNotifications, setEnablingNotifications] = useState(false);
    const [notifSettings, setNotifSettings] = useState({
        sms_alerts_enabled: false,
        high_risk_alerts_enabled: true,
        supervisor_phone: '',
        sms_configured: false
    });
    const [savingAlerts, setSavingAlerts] = useState(false);

    const role = user?.team_role || 'owner';
    const isOwner = role === 'owner';

    // Check notification status on mount
    React.useEffect(() => {
        const checkNotificationStatus = async () => {
            try {
                const response = await authAxios.get('/notifications/settings');
                setNotificationsEnabled(response.data.fcm_token_registered);
                setNotifSettings({
                    sms_alerts_enabled: response.data.sms_alerts_enabled || false,
                    high_risk_alerts_enabled: response.data.high_risk_alerts_enabled !== false,
                    supervisor_phone: response.data.supervisor_phone || '',
                    sms_configured: response.data.sms_configured || false
                });
            } catch (err) {
                console.log('Could not check notification status:', err.message);
            }
        };
        if (user && authAxios) {
            checkNotificationStatus();
        }
    }, [user, authAxios]);

    const saveAlertSettings = async () => {
        setSavingAlerts(true);
        try {
            const payload = {
                daily_summary_enabled: true,
                daily_summary_hour: 22,
                high_risk_alerts_enabled: notifSettings.high_risk_alerts_enabled,
                sms_alerts_enabled: notifSettings.sms_alerts_enabled,
                whatsapp_alerts_enabled: false,
                supervisor_phone: notifSettings.supervisor_phone?.trim() || null
            };
            await authAxios.put('/notifications/settings', payload);
            toast.success(language === 'en' ? 'Alert settings saved' : 'अलर्ट सेटिंग्स सहेजी गईं');
            setAlertsModalOpen(false);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to save');
        } finally {
            setSavingAlerts(false);
        }
    };

    const handleToggleNotifications = async (enabled) => {
        console.log('Toggle notifications called:', enabled);
        setEnablingNotifications(true);
        
        try {
            if (enabled) {
                // Check browser support
                const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
                const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
                
                if (isIOS) {
                    alert(language === 'en' 
                        ? 'Push notifications are not supported on iOS Safari. Please use Chrome on Android or desktop for notifications.' 
                        : 'iOS Safari पर पुश नोटिफिकेशन सपोर्ट नहीं है। कृपया Android या डेस्कटॉप पर Chrome का उपयोग करें।');
                    setEnablingNotifications(false);
                    return;
                }

                if (!('Notification' in window)) {
                    alert(language === 'en' 
                        ? 'Your browser does not support push notifications. Please use Chrome or Firefox.' 
                        : 'आपका ब्राउज़र पुश नोटिफिकेशन सपोर्ट नहीं करता। कृपया Chrome या Firefox का उपयोग करें।');
                    setEnablingNotifications(false);
                    return;
                }

                // Request permission
                console.log('Requesting notification permission...');
                const permission = await Notification.requestPermission();
                console.log('Permission result:', permission);
                
                if (permission !== 'granted') {
                    alert(language === 'en' 
                        ? 'Please allow notifications when prompted' 
                        : 'कृपया प्रॉम्प्ट होने पर नोटिफिकेशन की अनुमति दें');
                    setEnablingNotifications(false);
                    return;
                }

                // Try to get FCM token
                let fcmToken = null;
                try {
                    fcmToken = await requestNotificationPermission();
                } catch (fcmError) {
                    console.error('FCM error:', fcmError);
                }
                
                // Use FCM token or fallback
                const tokenToRegister = fcmToken || `browser_${Date.now()}_${user?.id}`;
                console.log('Registering token:', tokenToRegister.substring(0, 30) + '...');
                
                await authAxios.post('/notifications/register-fcm-token', { fcm_token: tokenToRegister });
                setNotificationsEnabled(true);
                
                // Show success notification
                new Notification('ShieldPayments', {
                    body: language === 'en' ? 'Notifications enabled!' : 'नोटिफिकेशन सक्षम!',
                    icon: '/logo192.png'
                });
                
                console.log('Notifications enabled successfully');
            } else {
                console.log('Disabling notifications...');
                await authAxios.delete('/notifications/unregister-fcm-token');
                setNotificationsEnabled(false);
                console.log('Notifications disabled');
            }
        } catch (err) {
            console.error('Error toggling notifications:', err);
            alert(language === 'en' ? 'Error updating notification settings' : 'नोटिफिकेशन सेटिंग अपडेट करने में त्रुटि');
        } finally {
            setEnablingNotifications(false);
        }
    };

    const handleSaveProfile = async () => {
        setSaving(true);
        try {
            await updateProfile(formData);
            setEditModalOpen(false);
        } catch (err) {
            console.error('Save profile error:', err);
        } finally {
            setSaving(false);
        }
    };

    const handleUpgrade = async (planId) => {
        try {
            await authAxios.post('/subscriptions/upgrade', { plan: planId });
            setSubscriptionModalOpen(false);
            // Refresh user data
            window.location.reload();
        } catch (err) {
            console.error('Upgrade error:', err);
        }
    };

    const currentPlan = PLANS.find(p => p.id === user?.subscription_plan) || PLANS[0];

    return (
        <MobileLayout>
            <div className="space-y-6" data-testid="settings-page">
                {/* Header */}
                <h1 className="text-2xl font-bold font-heading">{t('settings')}</h1>

                {/* Profile Section */}
                <Card data-testid="profile-card">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                                <User className="w-8 h-8 text-primary" />
                            </div>
                            <div className="flex-1">
                                <h3 className="font-semibold">
                                    {user?.owner_name || user?.business_name || (language === 'en' ? 'Merchant' : 'व्यापारी')}
                                </h3>
                                <p className="text-sm text-muted-foreground">{user?.phone}</p>
                                {user?.business_name && (
                                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                                        <Building className="w-3 h-3" />
                                        {user.business_name}
                                    </p>
                                )}
                            </div>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setEditModalOpen(true)}
                                data-testid="edit-profile-btn"
                            >
                                {t('edit')}
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                {/* Subscription Section */}
                <Card className="overflow-hidden" data-testid="subscription-card">
                    <CardHeader className="pb-2 bg-gradient-to-r from-primary/10 to-primary/5">
                        <CardTitle className="text-base flex items-center gap-2">
                            <Crown className="w-5 h-5 text-primary" />
                            {t('subscription')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="font-semibold">
                                    {language === 'en' ? currentPlan.name : currentPlan.nameHi} {t('currentPlan')}
                                </p>
                                <p className="text-sm text-muted-foreground">
                                    {currentPlan.price === 0 
                                        ? (language === 'en' ? 'Free forever' : 'हमेशा के लिए फ्री')
                                        : `₹${currentPlan.price}${t('perMonth')}`
                                    }
                                </p>
                            </div>
                            {currentPlan.id !== 'business' && (
                                <Button
                                    onClick={() => setSubscriptionModalOpen(true)}
                                    data-testid="upgrade-btn"
                                >
                                    {t('upgrade')}
                                </Button>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* Preferences */}
                <Card data-testid="preferences-card">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base">
                            {language === 'en' ? 'Preferences' : 'प्राथमिकताएं'}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {/* Language Toggle */}
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <Globe className="w-5 h-5 text-muted-foreground" />
                                <div>
                                    <p className="font-medium">{t('language')}</p>
                                    <p className="text-sm text-muted-foreground">
                                        {language === 'en' ? 'English' : 'हिंदी'}
                                    </p>
                                </div>
                            </div>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={toggleLanguage}
                                data-testid="language-toggle-btn"
                            >
                                {language === 'en' ? 'हिंदी' : 'English'}
                            </Button>
                        </div>

                        <Separator />

                        {/* Dark Mode Toggle */}
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                {isDark ? (
                                    <Moon className="w-5 h-5 text-muted-foreground" />
                                ) : (
                                    <Sun className="w-5 h-5 text-muted-foreground" />
                                )}
                                <div>
                                    <p className="font-medium">{t('darkMode')}</p>
                                    <p className="text-sm text-muted-foreground">
                                        {isDark 
                                            ? (language === 'en' ? 'On' : 'चालू')
                                            : (language === 'en' ? 'Off' : 'बंद')
                                        }
                                    </p>
                                </div>
                            </div>
                            <Switch
                                checked={isDark}
                                onCheckedChange={toggleTheme}
                                data-testid="dark-mode-toggle"
                            />
                        </div>

                        <Separator />

                        {/* Notifications */}
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <Bell className="w-5 h-5 text-muted-foreground" />
                                <div>
                                    <p className="font-medium">{t('notifications')}</p>
                                    <p className="text-sm text-muted-foreground">
                                        {enablingNotifications 
                                            ? (language === 'en' ? 'Enabling...' : 'सक्षम हो रहा है...')
                                            : notificationsEnabled 
                                                ? (language === 'en' ? 'Push notifications enabled' : 'पुश नोटिफिकेशन सक्षम')
                                                : (language === 'en' ? 'Enable push notifications' : 'पुश नोटिफिकेशन सक्षम करें')
                                        }
                                    </p>
                                </div>
                            </div>
                            <Switch 
                                checked={notificationsEnabled} 
                                onCheckedChange={(checked) => {
                                    console.log('Switch onCheckedChange triggered:', checked);
                                    handleToggleNotifications(checked);
                                }}
                                disabled={enablingNotifications}
                                data-testid="notifications-toggle" 
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Business features - Team + Inventory + Alerts */}
                <Card data-testid="business-features-card">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base">
                            {language === 'en' ? 'Business' : 'व्यवसाय'}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4 pt-0 space-y-2">
                        <button
                            className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition text-left"
                            onClick={() => navigate('/team')}
                            data-testid="team-link"
                        >
                            <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                                <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="font-medium">{language === 'en' ? 'Team' : 'टीम'}</p>
                                <p className="text-xs text-muted-foreground">
                                    {language === 'en' ? 'Add staff with roles' : 'भूमिकाओं के साथ स्टाफ जोड़ें'}
                                </p>
                            </div>
                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                        </button>

                        <button
                            className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition text-left"
                            onClick={() => navigate('/inventory')}
                            data-testid="inventory-link"
                        >
                            <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                                <Package className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="font-medium">{language === 'en' ? 'Inventory' : 'इन्वेंट्री'}</p>
                                <p className="text-xs text-muted-foreground">
                                    {language === 'en' ? 'Track stock & low-stock alerts' : 'स्टॉक और लो-स्टॉक ट्रैक करें'}
                                </p>
                            </div>
                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                        </button>

                        {isOwner && (
                            <button
                                className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition text-left"
                                onClick={() => setAlertsModalOpen(true)}
                                data-testid="sms-alerts-link"
                            >
                                <div className="w-10 h-10 rounded-lg bg-rose-500/10 flex items-center justify-center">
                                    <MessageSquareWarning className="w-5 h-5 text-rose-600 dark:text-rose-400" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="font-medium">{language === 'en' ? 'SMS Fraud Alerts' : 'SMS धोखाधड़ी अलर्ट'}</p>
                                    <p className="text-xs text-muted-foreground">
                                        {notifSettings.sms_alerts_enabled
                                            ? (language === 'en' ? 'On – high-risk txns sent via SMS' : 'चालू – उच्च जोखिम SMS भेजा जाएगा')
                                            : (language === 'en' ? 'Off – enable to get real-time SMS' : 'बंद – सक्षम करें')}
                                    </p>
                                </div>
                                <ChevronRight className="w-4 h-4 text-muted-foreground" />
                            </button>
                        )}
                    </CardContent>
                </Card>

                {/* Logout Button */}
                <Button
                    variant="destructive"
                    className="w-full"
                    onClick={logout}
                    data-testid="logout-btn"
                >
                    <LogOut className="w-5 h-5 mr-2" />
                    {t('logout')}
                </Button>

                {/* Disclaimer */}
                <div className="disclaimer-banner text-center flex items-center gap-2 justify-center">
                    <Shield className="w-4 h-4" />
                    {t('disclaimer')}
                </div>

                {/* Privacy Policy Link */}
                <div className="text-center">
                    <Link 
                        to="/privacy" 
                        className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1"
                        data-testid="privacy-policy-link"
                    >
                        <ExternalLink className="w-3 h-3" />
                        {language === 'en' ? 'Privacy Policy' : 'गोपनीयता नीति'}
                    </Link>
                </div>

                {/* Edit Profile Modal */}
                <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
                    <DialogContent data-testid="edit-profile-modal">
                        <DialogHeader>
                            <DialogTitle>
                                {language === 'en' ? 'Edit Profile' : 'प्रोफाइल संपादित करें'}
                            </DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                            <div>
                                <label className="text-sm font-medium">{t('ownerName')}</label>
                                <Input
                                    value={formData.owner_name}
                                    onChange={(e) => setFormData(prev => ({ ...prev, owner_name: e.target.value }))}
                                    placeholder={language === 'en' ? 'Your name...' : 'आपका नाम...'}
                                    className="mt-1"
                                    data-testid="owner-name-input"
                                />
                            </div>
                            <div>
                                <label className="text-sm font-medium">{t('businessName')}</label>
                                <Input
                                    value={formData.business_name}
                                    onChange={(e) => setFormData(prev => ({ ...prev, business_name: e.target.value }))}
                                    placeholder={language === 'en' ? 'Shop/Business name...' : 'दुकान/व्यवसाय का नाम...'}
                                    className="mt-1"
                                    data-testid="business-name-input"
                                />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setEditModalOpen(false)}>
                                {t('cancel')}
                            </Button>
                            <Button onClick={handleSaveProfile} disabled={saving} data-testid="save-profile-btn">
                                {t('save')}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Subscription Modal */}
                <Dialog open={subscriptionModalOpen} onOpenChange={setSubscriptionModalOpen}>                    <DialogContent className="max-w-lg" data-testid="subscription-modal">
                        <DialogHeader>
                            <DialogTitle className="text-center">
                                {language === 'en' ? 'Choose Your Plan' : 'अपना प्लान चुनें'}
                            </DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                            {PLANS.map((plan) => (
                                <Card 
                                    key={plan.id}
                                    className={cn(
                                        "cursor-pointer transition-all",
                                        plan.popular && "ring-2 ring-primary",
                                        user?.subscription_plan === plan.id && "bg-primary/5"
                                    )}
                                    onClick={() => plan.id !== user?.subscription_plan && handleUpgrade(plan.id)}
                                    data-testid={`plan-${plan.id}`}
                                >
                                    <CardContent className="p-4">
                                        <div className="flex items-start justify-between">
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <h3 className="font-bold">
                                                        {language === 'en' ? plan.name : plan.nameHi}
                                                    </h3>
                                                    {plan.popular && (
                                                        <Badge className="bg-primary">
                                                            {language === 'en' ? 'Popular' : 'लोकप्रिय'}
                                                        </Badge>
                                                    )}
                                                </div>
                                                <p className="text-2xl font-bold font-number mt-1">
                                                    {plan.price === 0 
                                                        ? (language === 'en' ? 'Free' : 'फ्री')
                                                        : `₹${plan.price}`
                                                    }
                                                    {plan.price > 0 && (
                                                        <span className="text-sm font-normal text-muted-foreground">
                                                            {t('perMonth')}
                                                        </span>
                                                    )}
                                                </p>
                                                <ul className="mt-3 space-y-1">
                                                    {plan.features.map((feature, idx) => (
                                                        <li key={idx} className="text-sm text-muted-foreground flex items-center gap-2">
                                                            <Check className="w-4 h-4 text-emerald-500" />
                                                            {language === 'en' ? feature.en : feature.hi}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                            {user?.subscription_plan === plan.id && (
                                                <Badge variant="outline" className="bg-emerald-50 text-emerald-600 border-emerald-500">
                                                    {t('currentPlan')}
                                                </Badge>
                                            )}
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </DialogContent>
                </Dialog>

                {/* SMS Alerts Modal */}
                <Dialog open={alertsModalOpen} onOpenChange={setAlertsModalOpen}>
                    <DialogContent data-testid="sms-alerts-modal">
                        <DialogHeader>
                            <DialogTitle>
                                {language === 'en' ? 'SMS Fraud Alerts' : 'SMS धोखाधड़ी अलर्ट'}
                            </DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 py-2">
                            <div className="flex items-center justify-between border rounded-lg p-3">
                                <div>
                                    <p className="font-medium">
                                        {language === 'en' ? 'Enable SMS alerts' : 'SMS अलर्ट सक्षम करें'}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                        {language === 'en'
                                            ? 'Get an SMS whenever a high-risk transaction is detected'
                                            : 'उच्च जोखिम लेन-देन पर SMS प्राप्त करें'}
                                    </p>
                                </div>
                                <Switch
                                    checked={notifSettings.sms_alerts_enabled}
                                    onCheckedChange={(v) => setNotifSettings({ ...notifSettings, sms_alerts_enabled: v })}
                                    data-testid="sms-alerts-toggle"
                                />
                            </div>

                            <div className="flex items-center justify-between border rounded-lg p-3">
                                <div>
                                    <p className="font-medium">
                                        {language === 'en' ? 'High-risk only' : 'केवल उच्च जोखिम'}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                        {language === 'en'
                                            ? 'Only send SMS for high-risk (recommended)'
                                            : 'केवल उच्च जोखिम पर SMS भेजें'}
                                    </p>
                                </div>
                                <Switch
                                    checked={notifSettings.high_risk_alerts_enabled}
                                    onCheckedChange={(v) => setNotifSettings({ ...notifSettings, high_risk_alerts_enabled: v })}
                                    data-testid="high-risk-only-toggle"
                                />
                            </div>

                            <div>
                                <label className="text-sm font-medium">
                                    {language === 'en' ? 'Supervisor phone (optional)' : 'सुपरवाइज़र फ़ोन (वैकल्पिक)'}
                                </label>
                                <Input
                                    value={notifSettings.supervisor_phone}
                                    onChange={(e) => setNotifSettings({ ...notifSettings, supervisor_phone: e.target.value })}
                                    placeholder="+919876543210"
                                    className="mt-1 font-number"
                                    data-testid="supervisor-phone-input"
                                />
                                <p className="text-xs text-muted-foreground mt-1">
                                    {language === 'en'
                                        ? 'A copy of every high-risk alert will also be sent to this number.'
                                        : 'हर उच्च जोखिम अलर्ट की एक कॉपी इस नंबर पर भी भेजी जाएगी।'}
                                </p>
                            </div>

                            {!notifSettings.sms_configured && (
                                <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                                    <p className="text-xs text-amber-800 dark:text-amber-300">
                                        {language === 'en'
                                            ? 'Note: Twilio sender is not yet configured on the server. Enable the toggle and save your settings — SMS will start delivering once TWILIO_ACCOUNT_SID / TWILIO_AUTH_TOKEN / TWILIO_FROM_NUMBER (or TWILIO_MESSAGING_SERVICE_SID) are set in the backend env.'
                                            : 'नोट: Twilio सेंडर अभी सेट नहीं है। सेटिंग सहेजें — बैकएंड env में क्रेडेंशियल जोड़ने पर SMS शुरू होगा।'}
                                    </p>
                                </div>
                            )}
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setAlertsModalOpen(false)}>
                                {t('cancel')}
                            </Button>
                            <Button onClick={saveAlertSettings} disabled={savingAlerts} data-testid="save-alert-settings-btn">
                                {savingAlerts ? '...' : t('save')}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </MobileLayout>
    );
};

export default Settings;
